import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, lineReveal } from "../lib/motion";
import { useSearch } from "../lib/search";
import { useProducts } from "../lib/products";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";

const TRENDING = ["OVERSIZED", "HOODIE", "CARGO", "GRAPHIC TEE", "BLACK"];

const HERO_IMG = "/api/images/rvior/products/8e2d5fb7039d453dbcd849329b9e56b6.jpg";
const EDITORIAL_IMG = "https://images.unsplash.com/photo-1508216310976-c518daae0cdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBvdmVyc2l6ZWQlMjBob29kaWUlMjBmYXNoaW9ufGVufDB8fHx8MTc4OTM3MjA5Mnww&ixlib=rb-4.1.0&q=85";
const LOOKBOOK_IMG = "https://images.unsplash.com/photo-1540297625533-d73b79e22d02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NDk1Nzl8MHwxfHNlYXJjaHwyfHxzdHJlZWFyJTIwdXJiYW4lMjBlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcG9zZXxlbnwwfHx8fDE3ODkzNzIwOTJ8MA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  {
    n: "01", title: "MATERIALITY",
    body: "280-420 GSM loopknit. Combed cotton, French terry, heavy twill — pre-washed, garment-dyed and built to outlive the feed. If it doesn't hold structure off the body, it doesn't carry the RVIOR stamp.",
  },
  {
    n: "02", title: "THE NEW DELHI UNDERGROUND",
    body: "Cut between rooftop warehouses and night markets. Every graphic references the city's static — grid noise, dispatch slips, metro wire. Not inspired by the underground. Emitted from it.",
  },
  {
    n: "03", title: "HYPER-DISPATCH TECH",
    body: "Every unit is made to order the moment you order — quality-checked, poly-bagged and dispatched pan-India within 24-48 hours. Zero deadstock, zero waste.",
  },
];

function Hero() {
  const ref = useRef(null);
  const tilt = useRef({ rx: 0, ry: 0 });
  const [tiltState, setTiltState] = useState({ rx: 0, ry: 0 });
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 90]);
  const yText = useTransform(scrollY, [0, 700], [0, 40]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setTiltState({
      rx: ((e.clientY - r.top) / r.height - 0.5) * -6,
      ry: ((e.clientX - r.left) / r.width - 0.5) * 6,
    });
  };

  return (
    <section ref={ref} onMouseMove={onMouseMove} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-10 px-5 md:px-10">
      <div className="grid lg:grid-cols-12 gap-10 items-center max-w-[1600px] mx-auto w-full">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-6">RVIOR® DROP 001</p>
          <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.92] text-[7.6vw] sm:text-[7.5vw] lg:text-[4.7vw]">
            <span className="mask-line"><motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block whitespace-nowrap">SUBVERSIVE</motion.span></span>
            <span className="mask-line pb-[0.14em] -mb-[0.06em]"><motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="block whitespace-nowrap font-edit italic font-medium text-acid normal-case tracking-normal text-[0.82em] leading-[1.05]">luxury&nbsp;/</motion.span></span>
            <span className="mask-line"><motion.span custom={2} variants={lineReveal} initial="hidden" animate="show" className="block whitespace-nowrap">STREETWEAR</motion.span></span>
          </h1>
          <motion.p custom={3} variants={lineReveal} initial="hidden" animate="show" className="mt-7 max-w-md text-dim text-sm md:text-base leading-relaxed">
            Heavyweight oversized silhouettes for the ones who move quiet and dress loud. Printed on demand. Dispatched pan-India.
          </motion.p>
          <motion.div custom={4} variants={lineReveal} initial="hidden" animate="show" className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/shop"
              data-testid="hero-cta-shop-drop"
              className="group inline-flex items-center gap-3 bg-volt text-void font-display font-bold text-sm tracking-widest px-8 py-4 hover:bg-acid transition-colors duration-300"
            >
              SHOP DROP 001
              <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <a
              href="#lookbook"
              data-testid="hero-cta-lookbook"
              onClick={(e) => { e.preventDefault(); document.getElementById("lookbook")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-3 border border-white/20 font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-acid hover:text-acid transition-colors duration-300"
            >
              READ THE MANIFESTO
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}>
          <motion.div
            style={{ transform: `perspective(900px) rotateX(${tiltState.rx}deg) rotateY(${tiltState.ry}deg)` }}
            transition={{ type: "spring", stiffness: 60, damping: 14 }}
            className="relative clip-frame border border-white/10"
          >
            <img src={HERO_IMG} alt="RVIOR Drop 001 editorial" className="w-full aspect-[3/2] lg:aspect-[3/2] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060709]/60 via-transparent to-transparent" />
            <p className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] bg-void/70 backdrop-blur px-2 py-1">DROP_001 / LOOK 01</p>
          </motion.div>
        </motion.div>
      </div>

      <div className="mt-12 max-w-[1600px] mx-auto w-full grid grid-cols-2 md:grid-cols-4 border border-white/10 font-mono text-[10px] tracking-[0.2em] text-dim">
        {["280 GSM LOOPKNIT", "OVERSIZED SILHOUETTES", "PRINTED ON DEMAND", "DISPATCH PAN-INDIA"].map((t) => (
          <div key={t} className="border-r border-white/10 last:border-r-0 px-4 py-3 text-center">{t}</div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { products, state, retry } = useProducts();
  const location = useLocation();
  const { openSearch } = useSearch();

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.state]);

  return (
    <main>
      <Hero />

      <Marquee
        className="my-2"
        items={["ARCHIVAL GRADE 320 GSM LOOPKNIT", "CRAFTED IN NEW DELHI", "MADE TO ORDER — DISPATCHED IN 24-48H", "DROP 001 ACTIVE"]}
      />

      {/* TRENDING SEARCHES */}
      <section data-testid="home-trending-strip" className="px-5 md:px-10 pt-12 max-w-[1600px] mx-auto flex flex-col md:flex-row md:items-center gap-4">
        <p className="font-mono text-[11px] tracking-[0.3em] text-volt shrink-0">TRENDING SEARCHES →</p>
        <div className="flex flex-wrap gap-2">
          {TRENDING.map((t) => (
            <button
              key={t}
              data-testid={`home-trending-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => openSearch(t)}
              className="font-mono text-[11px] tracking-[0.2em] border border-white/15 px-4 py-2 text-dim hover:border-acid hover:text-acid transition-colors duration-300"
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      {/* DROP CATALOGUE */}
      <section className="px-5 md:px-10 py-20 md:py-28 max-w-[1600px] mx-auto">
        <Reveal className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-3">01 — THE CATALOGUE</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl">DROP 001 / CURRENT UNITS</h2>
          </div>
          <Link to="/shop" className="font-mono text-[11px] tracking-[0.25em] border border-white/20 px-6 py-3 hover:border-acid hover:text-acid transition-colors duration-300">
            VIEW FULL GRID →
          </Link>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min">
          {products.map((p, i) => (
            <Reveal key={p._id} delay={(i % 3) * 0.08} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
              <div className="h-full [&>a]:h-full">
                <ProductCard product={p} big={i === 0} />
              </div>
            </Reveal>
          ))}
          {state === "error" ? (
            <div data-testid="home-catalogue-error" className="col-span-full py-20 text-center">
              <p className="font-display text-lg font-bold uppercase">COULDN'T LOAD THE DROP</p>
              <p className="font-mono text-[11px] tracking-widest text-mute mt-2">CHECK YOUR CONNECTION AND TRY AGAIN</p>
              <button data-testid="home-catalogue-retry-btn" onClick={retry} className="mt-6 font-mono text-[11px] tracking-[0.25em] border border-white/15 px-6 py-3 text-dim hover:border-acid hover:text-acid transition-colors duration-300">RETRY</button>
            </div>
          ) : products.length === 0 && (
            <p className="font-mono text-[11px] tracking-widest text-mute col-span-full py-20 text-center">LOADING THE DROP…</p>
          )}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="px-5 md:px-10 py-20 md:py-28 max-w-[1600px] mx-auto border-t border-white/10">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-3">02 — THE MANIFESTO</p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-14">
            THREE CHAPTERS. <span className="font-edit italic font-medium text-acid normal-case">no compromises.</span>
          </h2>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08} className={`flex gap-6 py-8 border-b border-white/10 ${i === 0 ? "pt-0" : ""}`}>
                <span className="font-mono text-volt text-sm pt-2">{c.n}</span>
                <div>
                  <h3 className="font-display font-bold uppercase text-lg md:text-xl tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-dim text-sm leading-relaxed max-w-md">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="relative">
            <div className="clip-frame border border-white/10 h-full min-h-[380px] lg:sticky lg:top-24">
              <img src={EDITORIAL_IMG} alt="RVIOR manifesto editorial" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060709]/70 to-transparent" />
              <p className="absolute bottom-5 left-5 font-edit italic text-lg text-white/90">"Not merch. Engineered street culture."</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LOOKBOOK */}
      <section id="lookbook" className="relative h-[70vh] overflow-hidden border-y border-white/10">
        <motion.img
          src={LOOKBOOK_IMG}
          alt="RVIOR lookbook"
          className="absolute inset-0 w-full h-[130%] object-cover"
          initial={{ y: -60 }}
          whileInView={{ y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0 bg-[#060709]/55 flex items-center justify-center">
          <Reveal className="text-center px-5">
            <p className="font-mono text-[11px] tracking-[0.3em] text-acid mb-4">LOOKBOOK / WET ASPHALT SERIES</p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-5xl lg:text-6xl leading-none">
              ENGINEERED IN THE<br />UNDERGROUND
            </h2>
            <Link to="/shop" className="inline-block mt-8 bg-ink text-void font-display font-bold text-sm tracking-widest px-8 py-4 hover:bg-acid transition-colors duration-300">
              SHOP THE LOOKS
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
